import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  Textarea,
  PageHeader,
  Spinner,
  EmptyState,
  Modal,
  Field,
  Select,
  Badge,
  Tabs,
  ErrorBanner,
  formatDateTime,
} from '../components/ui';

interface PhoneNumber {
  id: string;
  number: string;
  label?: string;
  capabilities: string[];
  ivrId?: string;
}

interface Call {
  id: string;
  fromNumber: string;
  toNumber: string;
  direction: 'inbound' | 'outbound';
  durationSec?: number;
  status: string;
  startedAt: string;
}

interface IvrFlow {
  id: string;
  name: string;
  greeting?: string;
  options?: { digit: string; action: string; target?: string }[];
}

interface VoicemailDrop {
  id: string;
  name: string;
  audioUrl?: string;
}

interface DialCampaign {
  id: string;
  name: string;
  status: 'draft' | 'running' | 'completed';
  totalNumbers: number;
}

type Tab = 'numbers' | 'calls' | 'ivr' | 'drops' | 'campaigns';

export function VoicePage() {
  const [tab, setTab] = useState<Tab>('numbers');
  return (
    <div>
      <PageHeader title="Voice" subtitle="Phone numbers, IVR, calls, and dial campaigns" />
      <Tabs<Tab>
        active={tab}
        onChange={setTab}
        tabs={[
          { value: 'numbers', label: 'Numbers' },
          { value: 'calls', label: 'Calls' },
          { value: 'ivr', label: 'IVR' },
          { value: 'drops', label: 'Voicemail drops' },
          { value: 'campaigns', label: 'Dial campaigns' },
        ]}
      />
      {tab === 'numbers' && <Numbers />}
      {tab === 'calls' && <Calls />}
      {tab === 'ivr' && <Ivr />}
      {tab === 'drops' && <Drops />}
      {tab === 'campaigns' && <Campaigns />}
    </div>
  );
}

function Numbers() {
  const [showCreate, setShowCreate] = useState(false);
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['phone-numbers'],
    queryFn: () => api.get<{ data: PhoneNumber[] }>('/voice/phone-numbers').catch(() => ({ data: [] })),
  });
  const numbers = q.data?.data ?? [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-3)' }}>
        <Button onClick={() => setShowCreate(true)}>+ Provision number</Button>
      </div>
      {q.isLoading ? (
        <Spinner />
      ) : numbers.length === 0 ? (
        <Card>
          <EmptyState title="No phone numbers" description="Add a phone number to start receiving calls and SMS." action={<Button onClick={() => setShowCreate(true)}>+ Provision number</Button>} />
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {numbers.map((n) => (
            <Card key={n.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{n.number}</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                    {n.label ?? '(no label)'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                  {n.capabilities.map((c) => (
                    <Badge key={c} color="blue">
                      {c}
                    </Badge>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      {showCreate && (
        <ProvisionModal onClose={() => setShowCreate(false)} onSaved={() => { qc.invalidateQueries({ queryKey: ['phone-numbers'] }); setShowCreate(false); }} />
      )}
    </div>
  );
}

function ProvisionModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [number, setNumber] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () =>
      api.post('/voice/phone-numbers', {
        twilioSid: `MANUAL_${Date.now()}`,
        number,
        friendlyName: label || undefined,
        capabilities: { voice: true, sms: true, mms: false },
      }),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed'),
  });
  return (
    <Modal onClose={onClose} title="Provision phone number">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!number) return setError('Number required');
          mut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="Number *">
          <Input value={number} onChange={setNumber} placeholder="+1 555 123 4567" autoFocus />
        </Field>
        <Field label="Label">
          <Input value={label} onChange={setLabel} placeholder="Main line" />
        </Field>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Provisioning…' : 'Provision'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function Calls() {
  const q = useQuery({
    queryKey: ['calls'],
    queryFn: () => api.get<{ data: Call[] }>('/voice/calls').catch(() => ({ data: [] })),
  });
  const calls = q.data?.data ?? [];
  if (q.isLoading) return <Spinner />;
  if (calls.length === 0) return <Card><EmptyState title="No calls yet" /></Card>;
  return (
    <Card style={{ padding: 0 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--color-surface-secondary)', textAlign: 'left' }}>
            <th style={cellStyle}>Direction</th>
            <th style={cellStyle}>From</th>
            <th style={cellStyle}>To</th>
            <th style={cellStyle}>Duration</th>
            <th style={cellStyle}>Status</th>
            <th style={cellStyle}>Started</th>
          </tr>
        </thead>
        <tbody>
          {calls.map((c) => (
            <tr key={c.id} style={{ borderTop: '1px solid var(--color-border-primary)' }}>
              <td style={cellStyle}>{c.direction === 'inbound' ? '↓' : '↑'}</td>
              <td style={cellStyle}>{c.fromNumber}</td>
              <td style={cellStyle}>{c.toNumber}</td>
              <td style={cellStyle}>{c.durationSec ? `${Math.round(c.durationSec / 60)} min` : '—'}</td>
              <td style={cellStyle}>{c.status}</td>
              <td style={cellStyle}>{formatDateTime(c.startedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function Ivr() {
  const [editing, setEditing] = useState<IvrFlow | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['ivr'],
    queryFn: () =>
      api
        .get<{ data: IvrFlow }>('/voice/ivr')
        .then((r) => ({ data: r.data ? [r.data] : [] }))
        .catch(() => ({ data: [] })),
  });
  const flows = q.data?.data ?? [];
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-3)' }}>
        <Button onClick={() => setShowCreate(true)}>+ New IVR</Button>
      </div>
      {q.isLoading ? (
        <Spinner />
      ) : flows.length === 0 ? (
        <Card><EmptyState title="No IVR flows" description="Build a phone tree to route incoming calls." /></Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {flows.map((f) => (
            <Card key={f.id} onClick={() => setEditing(f)}>
              <div style={{ fontWeight: 600 }}>{f.name}</div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                {f.options?.length ?? 0} options
              </div>
            </Card>
          ))}
        </div>
      )}
      {(showCreate || editing) && (
        <IvrModal flow={editing} onClose={() => { setShowCreate(false); setEditing(null); }} onSaved={() => { qc.invalidateQueries({ queryKey: ['ivr'] }); setShowCreate(false); setEditing(null); }} />
      )}
    </div>
  );
}

function IvrModal({ flow, onClose, onSaved }: { flow: IvrFlow | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(flow?.name ?? '');
  const [greeting, setGreeting] = useState(flow?.greeting ?? '');
  const [options, setOptions] = useState(flow?.options ?? [{ digit: '1', action: 'forward', target: '' }]);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => {
      const body = { name, greeting, options };
      return api.put('/voice/ivr', body);
    },
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed'),
  });

  return (
    <Modal onClose={onClose} title={flow ? 'Edit IVR' : 'New IVR'}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name) return setError('Name required');
          mut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="Name">
          <Input value={name} onChange={setName} />
        </Field>
        <Field label="Greeting">
          <Textarea value={greeting} onChange={setGreeting} rows={3} placeholder="Hi, you've reached…" />
        </Field>
        <div>
          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>
            Options
          </div>
          {options.map((opt, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '50px 1fr 2fr auto', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
              <Input value={opt.digit} onChange={(v) => setOptions(options.map((o, i) => (i === idx ? { ...o, digit: v } : o)))} placeholder="1" />
              <Select
                value={opt.action}
                onChange={(v) => setOptions(options.map((o, i) => (i === idx ? { ...o, action: v } : o)))}
                options={[
                  { value: 'forward', label: 'Forward call' },
                  { value: 'voicemail', label: 'Send to voicemail' },
                  { value: 'submenu', label: 'Submenu' },
                ]}
              />
              <Input value={opt.target ?? ''} onChange={(v) => setOptions(options.map((o, i) => (i === idx ? { ...o, target: v } : o)))} placeholder="Phone number or menu ID" />
              <Button size="sm" variant="ghost" onClick={() => setOptions(options.filter((_, i) => i !== idx))}>×</Button>
            </div>
          ))}
          <Button size="sm" variant="secondary" onClick={() => setOptions([...options, { digit: String(options.length + 1), action: 'forward', target: '' }])}>
            + Add option
          </Button>
        </div>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={mut.isPending}>{mut.isPending ? 'Saving…' : 'Save'}</Button>
        </div>
      </form>
    </Modal>
  );
}

function Drops() {
  const q = useQuery({
    queryKey: ['voicemail-drops'],
    queryFn: () => api.get<{ data: VoicemailDrop[] }>('/voice/voicemail-drops').catch(() => ({ data: [] })),
  });
  if (q.isLoading) return <Spinner />;
  const drops = q.data?.data ?? [];
  if (drops.length === 0) return <Card><EmptyState title="No voicemail drops" description="Pre-record voicemails to drop into outbound calls." /></Card>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      {drops.map((d) => (
        <Card key={d.id}>
          <div style={{ fontWeight: 600 }}>{d.name}</div>
          {d.audioUrl && <audio src={d.audioUrl} controls style={{ width: '100%', marginTop: 'var(--space-2)' }} />}
        </Card>
      ))}
    </div>
  );
}

function Campaigns() {
  const q = useQuery({
    queryKey: ['dial-campaigns'],
    queryFn: () => api.get<{ data: DialCampaign[] }>('/voice/campaigns').catch(() => ({ data: [] })),
  });
  if (q.isLoading) return <Spinner />;
  const items = q.data?.data ?? [];
  if (items.length === 0) return <Card><EmptyState title="No dial campaigns" description="Outbound dial campaigns will show here." /></Card>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      {items.map((c) => (
        <Card key={c.id}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 600 }}>{c.name}</div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                {c.totalNumbers} numbers
              </div>
            </div>
            <Badge color={c.status === 'running' ? 'green' : c.status === 'completed' ? 'blue' : 'gray'}>{c.status}</Badge>
          </div>
        </Card>
      ))}
    </div>
  );
}

const cellStyle: React.CSSProperties = {
  padding: 'var(--space-3)',
  fontSize: 'var(--text-sm)',
};
